import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Camera, CameraOff, Timer as TimerIcon, AlertTriangle, Clock, Wrench, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { glossaryTerms } from "../../data/glossary";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from "@/components/ui/tooltip";
export function StepsSection({ steps, glossaryTermsInRecipe, glossaryOverrides }) {
    const [showImages, setShowImages] = useState(true);
    const [checkedSteps, setCheckedSteps] = useState(new Set());
    const [activeTimers, setActiveTimers] = useState({});
    // Type guard to check if step is DetailedRecipeStep
    const isDetailedStep = (step) => {
        return 'method' in step;
    };
    const toggleStep = (id) => {
        setCheckedSteps((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const startTimer = (stepId, duration) => {
        setActiveTimers((prev) => (Object.assign(Object.assign({}, prev), { [stepId]: duration * 60 })));
    };
    // Timer countdown effect
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTimers((prev) => {
                const updated = Object.assign({}, prev);
                let hasChanges = false;
                Object.keys(updated).forEach((stepId) => {
                    if (updated[stepId] > 0) {
                        updated[stepId] -= 1;
                        hasChanges = true;
                    }
                    else if (updated[stepId] === 0) {
                        // Timer finished - could add notification here
                        delete updated[stepId];
                        hasChanges = true;
                    }
                });
                return hasChanges ? updated : prev;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);
    const formatTimerDisplay = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };
    // Get glossary definition with override support
    const getGlossaryDefinition = (term) => {
        // Check for override first
        if (glossaryOverrides) {
            const override = glossaryOverrides.find((o) => o.key.toLowerCase() === term.toLowerCase());
            if (override && override.override_definition) {
                return { term: override.key, definition: override.override_definition };
            }
        }
        // Fall back to main glossary
        const termData = glossaryTerms.find((t) => t.term.toLowerCase() === term.toLowerCase());
        return termData || null;
    };
    // Helper function to highlight glossary terms in text
    const highlightGlossaryTerms = (text) => {
        if (!text)
            return text;
        const termsToHighlight = glossaryTermsInRecipe.filter((term) => term && text.toLowerCase().includes(term.toLowerCase()));
        if (termsToHighlight.length === 0) {
            return text;
        }
        // Split text by terms and create elements
        let parts = [text];
        let keyCounter = 0;
        termsToHighlight.forEach((term) => {
            const termData = getGlossaryDefinition(term);
            if (termData) {
                const newParts = [];
                parts.forEach((part) => {
                    if (typeof part === "string") {
                        const regex = new RegExp(`(${term})`, "gi");
                        const segments = part.split(regex);
                        segments.forEach((segment) => {
                            if (segment.toLowerCase() === term.toLowerCase()) {
                                newParts.push(_jsx(TooltipProvider, { children: _jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx("span", { className: "underline decoration-dotted decoration-[#c85a3f] cursor-help", children: segment }) }), _jsxs(TooltipContent, { className: "max-w-xs", children: [_jsx("p", { className: "font-semibold", children: termData.term }), _jsx("p", { className: "text-sm", children: termData.definition })] })] }) }, `tooltip-${keyCounter++}`));
                            }
                            else if (segment) {
                                newParts.push(segment);
                            }
                        });
                    }
                    else {
                        newParts.push(part);
                    }
                });
                parts = newParts;
            }
        });
        return _jsx(_Fragment, { children: parts });
    };
    // Helper to get severity badge color
    const getSeverityColor = (severity) => {
        switch (severity) {
            case "critical":
            case "warning":
                return "bg-red-100 text-red-800 border-red-200";
            case "caution":
                return "bg-[#f5d77e]/30 text-[#5a4a3a] border-[#f5d77e]";
            case "info":
            default:
                return "bg-[#a8c5a4]/20 text-[#5a4a3a] border-[#a8c5a4]/40";
        }
    };
    // Helper to get mode badge
    const getModeBadge = (mode) => {
        switch (mode) {
            case "hands_on":
                return { label: "Hands-on", color: "bg-[#c85a3f]/10 text-[#c85a3f] border-[#c85a3f]/30" };
            case "hands_off":
                return { label: "Hands-off", color: "bg-blue-50 text-blue-700 border-blue-200" };
            case "passive":
                return { label: "Passive", color: "bg-gray-50 text-gray-700 border-gray-200" };
            case "prep":
                return { label: "Prep", color: "bg-green-50 text-green-700 border-green-200" };
            default:
                return { label: "Hands-on", color: "bg-[#c85a3f]/10 text-[#c85a3f] border-[#c85a3f]/30" };
        }
    };
    return (_jsxs("div", { className: "space-y-6 recipe-print-section recipe-print-steps", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("h2", { className: "text-[#5a4a3a]", children: "Instructions" }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => setShowImages(!showImages), className: "gap-2 no-print", children: showImages ? (_jsxs(_Fragment, { children: [_jsx(Camera, { className: "w-4 h-4" }), _jsx("span", { className: "hidden sm:inline", children: "Hide Images" })] })) : (_jsxs(_Fragment, { children: [_jsx(CameraOff, { className: "w-4 h-4" }), _jsx("span", { className: "hidden sm:inline", children: "Show Images" })] })) })] }), _jsx("div", { className: "space-y-6", children: steps.map((step, index) => {
                    const stepId = isDetailedStep(step) ? step.id.toString() : step.id;
                    const isChecked = checkedSteps.has(stepId);
                    const timerSeconds = activeTimers[stepId];
                    const isTimerActive = timerSeconds !== undefined;
                    // Get timer duration
                    const timerDuration = isDetailedStep(step) ? step.duration_min : step.timer;
                    return (_jsx("div", { className: `bg-[#faf8f5] border-2 border-[#e8e3db] rounded-lg p-6 transition-all shadow-[2px_2px_0_rgba(45,37,32,0.05)] ${isChecked ? "opacity-60" : ""}`, children: _jsxs("div", { className: "flex items-start gap-4", children: [_jsx(Checkbox, { id: stepId, checked: isChecked, onCheckedChange: () => toggleStep(stepId), className: "mt-1 flex-shrink-0" }), _jsxs("div", { className: "flex-1 space-y-3", children: [_jsxs("div", { className: "flex items-start gap-3 flex-wrap", children: [_jsx("div", { className: "flex-shrink-0 w-8 h-8 rounded-full bg-[#c85a3f] text-white flex items-center justify-center", children: isDetailedStep(step) ? step.id : step.stepNumber }), isDetailedStep(step) && (_jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [step.duration_min > 0 && (_jsxs("span", { className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border bg-background", children: [_jsx(Clock, { className: "w-3 h-3" }), "~", step.duration_min, " min"] })), step.mode_key && (_jsx("span", { className: `inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${getModeBadge(step.mode_key).color}`, children: getModeBadge(step.mode_key).label })), step.severity && step.severity !== "info" && (_jsxs("span", { className: `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${getSeverityColor(step.severity)}`, children: [_jsx(AlertTriangle, { className: "w-3 h-3" }), step.severity.toUpperCase()] }))] }))] }), _jsx("label", { htmlFor: stepId, className: `block cursor-pointer ${isChecked ? "line-through" : ""} text-[#5a4a3a]`, children: highlightGlossaryTerms(step.instruction) }), isDetailedStep(step) && step.method && (_jsx("div", { className: "text-sm text-[#5a4a3a]/80 leading-relaxed pl-11", children: highlightGlossaryTerms(step.method) })), isDetailedStep(step) && step.warning && (_jsxs("div", { className: "bg-[#f5d77e]/20 border border-[#f5d77e]/50 rounded-lg p-3 flex items-start gap-2 pl-11", children: [_jsx(AlertTriangle, { className: "w-4 h-4 text-[#c85a3f] flex-shrink-0 mt-0.5" }), _jsx("p", { className: "text-sm text-[#5a4a3a]", children: step.warning })] })), isDetailedStep(step) && (step.equipment.length > 0 || step.ingredients_used.length > 0) && (_jsxs("div", { className: "flex gap-4 flex-wrap text-xs pl-11", children: [step.equipment.length > 0 && (_jsxs("div", { className: "flex items-start gap-1.5 text-[#5a4a3a]/60", children: [_jsx(Wrench, { className: "w-3.5 h-3.5 mt-0.5 flex-shrink-0" }), _jsx("span", { children: step.equipment.join(", ") })] })), step.ingredients_used.length > 0 && (_jsxs("div", { className: "flex items-start gap-1.5 text-[#5a4a3a]/60", children: [_jsx(Package, { className: "w-3.5 h-3.5 mt-0.5 flex-shrink-0" }), _jsx("span", { children: step.ingredients_used.map((ing, i) => (_jsxs("span", { children: [i > 0 && ", ", ing.label || ing.ref, ing.amount > 0 && ` (${ing.amount} ${ing.unit})`] }, i))) })] }))] })), showImages && !isDetailedStep(step) && step.hasImage && step.imageUrl && (_jsx("img", { src: step.imageUrl, alt: `Step ${step.stepNumber}`, className: "w-full max-w-md rounded-lg border border-[#e8e3db]" })), timerDuration && timerDuration > 0 && (_jsx("div", { className: "flex items-center gap-2 pl-11", children: isTimerActive ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-2 px-3 py-2 bg-[#c85a3f] text-white rounded-md", children: [_jsx(TimerIcon, { className: "w-4 h-4" }), _jsx("span", { className: "font-mono", children: formatTimerDisplay(timerSeconds) })] }), _jsx(Button, { variant: "outline", size: "sm", onClick: () => {
                                                            setActiveTimers((prev) => {
                                                                const updated = Object.assign({}, prev);
                                                                delete updated[stepId];
                                                                return updated;
                                                            });
                                                        }, children: "Cancel" })] })) : (_jsxs(Button, { variant: "outline", size: "sm", onClick: () => startTimer(stepId, timerDuration), className: "gap-2", children: [_jsx(TimerIcon, { className: "w-4 h-4" }), "Start ", timerDuration, " min timer", !isDetailedStep(step) && step.timerLabel && ` (${step.timerLabel})`] })) }))] })] }) }, stepId));
                }) })] }));
}
